/**
 * Cart extras for the Horizon cart drawer and cart page (mrk-cart agent).
 * - <mrk-bundle-progress>: on the cart page, re-renders its section after cart changes (the drawer copy is
 *   re-rendered by Horizon's cart section morph).
 * - <mrk-cart-upsell>: Product Recommendations API (complementary → related) rendered through
 *   sections/mrk-cart-upsell.liquid; re-fetches when the cart's products change. On the cart page (data-section-id)
 *   it re-renders its section after cart changes, since Horizon's morph only covers the cart drawer and main cart.
 * Cart math stays Shopify's: nothing here computes prices.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const UPSELL_SECTION = 'mrk-cart-upsell';

/**
 * Re-renders the element's section (data-section-id) after each cart change. Used on the cart page only.
 * @param {HTMLElement} element
 * @returns {() => void} unsubscribe
 */
function refreshSectionOnCartUpdate(element) {
  /** @param {Event & { promise?: Promise<unknown> }} event */
  const onCartUpdate = (event) => {
    const sectionId = element.dataset.sectionId;
    if (!sectionId) return;
    Promise.resolve(event.promise)
      .then(() => sectionRenderer.renderSection(sectionId, { cache: false }))
      .catch(() => {});
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
  return () => document.removeEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
}

class MrkBundleProgress extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    if (this.dataset.sectionId) this.#unsubscribe = refreshSectionOnCartUpdate(this);
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }
}

class MrkCartUpsell extends HTMLElement {
  static observedAttributes = ['data-mrk-product-id', 'data-mrk-cart-products'];

  /** @type {AbortController | null} */
  #controller = null;
  #loadedKey = '';
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    if (this.dataset.sectionId) this.#unsubscribe = refreshSectionOnCartUpdate(this);
    this.#load();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#load();
  }

  get #key() {
    return `${this.dataset.mrkProductId}|${this.dataset.mrkCartProducts}`;
  }

  async #load() {
    const productId = this.dataset.mrkProductId;
    const slot = this.querySelector('[data-mrk-upsell-slot]');
    if (!productId || !slot || this.#key === this.#loadedKey) return;
    this.#loadedKey = this.#key;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const limit = Number(this.dataset.mrkLimit) || 6;
    const inCart = (this.dataset.mrkCartProducts || '').split(',').filter(Boolean).length;
    const base = this.dataset.mrkUrl || '/recommendations/products';

    try {
      let cards = [];
      for (const intent of ['complementary', 'related']) {
        const url = `${base}?product_id=${encodeURIComponent(productId)}&limit=${Math.min(
          limit + inCart,
          10
        )}&intent=${intent}&section_id=${UPSELL_SECTION}`;
        const response = await fetch(url, { signal });
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        cards = Array.from(doc.querySelectorAll('[data-mrk-upsell-items] > li')).slice(0, limit);
        if (cards.length) break;
      }
      if (signal.aborted) return;
      slot.replaceChildren(...cards.map((card) => document.importNode(card, true)));
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') slot.replaceChildren();
    }
  }
}

if (!customElements.get('mrk-bundle-progress')) customElements.define('mrk-bundle-progress', MrkBundleProgress);
if (!customElements.get('mrk-cart-upsell')) customElements.define('mrk-cart-upsell', MrkCartUpsell);
